import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { useCallback, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  completeConsultation,
  createPrescription,
  type CreatePrescriptionBody,
} from "@/api/appointmentApi";

interface MedicationRow {
  drugName: string;
  dosage: string;
  frequency: string;
  duration: string;
  instructions: string;
}

const emptyRow = (): MedicationRow => ({
  drugName: "",
  dosage: "",
  frequency: "",
  duration: "",
  instructions: "",
});

interface Props {
  open: boolean;
  appointmentId: string;
  onComplete: () => void;
  onError: (msg: string) => void;
}

export default function PrescriptionFormDialog({
  open,
  appointmentId,
  onComplete,
  onError,
}: Props) {
  const [diagnosis, setDiagnosis] = useState("");
  const [diagnosticReferral, setDiagnosticReferral] = useState("");
  const [remarks, setRemarks] = useState("");
  const [medications, setMedications] = useState<MedicationRow[]>([emptyRow()]);
  const [error, setError] = useState<string | null>(null);

  const updateMed = useCallback(
    (idx: number, field: keyof MedicationRow, value: string) => {
      setMedications((prev) => {
        const next = [...prev];
        next[idx] = { ...next[idx], [field]: value };
        return next;
      });
    },
    [],
  );

  const addMed = useCallback(() => {
    setMedications((prev) => [...prev, emptyRow()]);
  }, []);

  const removeMed = useCallback((idx: number) => {
    setMedications((prev) => prev.filter((_, i) => i !== idx));
  }, []);

  const submitMut = useMutation({
    mutationFn: async () => {
      const validMeds = medications.filter((m) => m.drugName.trim().length > 0);

      const body: CreatePrescriptionBody = {
        diagnosis: diagnosis.trim(),
        diagnosticReferral: diagnosticReferral.trim() || undefined,
        remarks: remarks.trim() || undefined,
        medications: validMeds.map((m) => ({
          drugName: m.drugName.trim(),
          dosage: m.dosage.trim() || undefined,
          frequency: m.frequency.trim() || undefined,
          duration: m.duration.trim() || undefined,
          instructions: m.instructions.trim() || undefined,
        })),
      };

      await createPrescription(appointmentId, body);
      const doctorRemark = [
        diagnosis.trim() ? `Diagnosis: ${diagnosis.trim()}` : "",
        remarks.trim() || "",
      ]
        .filter(Boolean)
        .join("\n") || "Consultation completed with prescription.";

      await completeConsultation(appointmentId, doctorRemark);
    },
    onSuccess: () => onComplete(),
    onError: (err: unknown) => {
      const msg =
        err instanceof Error ? err.message : "Failed to save prescription.";
      setError(msg);
      onError(msg);
    },
  });

  const skipMut = useMutation({
    mutationFn: async () => {
      const remark = remarks.trim() || "Consultation completed.";
      await completeConsultation(appointmentId, remark);
    },
    onSuccess: () => onComplete(),
    onError: (err: unknown) => {
      const msg =
        err instanceof Error ? err.message : "Failed to complete consultation.";
      setError(msg);
      onError(msg);
    },
  });

  const canSubmit =
    diagnosis.trim().length > 0 && !submitMut.isPending && !skipMut.isPending;

  return (
    <Dialog open={open} fullWidth maxWidth="md" disableEscapeKeyDown>
      <DialogTitle>Complete Consultation — Prescription</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Add the prescription details below. The patient will receive this as a
          downloadable PDF and via email.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <TextField
          label="Diagnosis"
          value={diagnosis}
          onChange={(e) => setDiagnosis(e.target.value)}
          fullWidth
          required
          multiline
          rows={2}
          sx={{ mb: 2 }}
          placeholder="e.g. Upper respiratory tract infection"
        />

        <Divider sx={{ my: 2 }} />

        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 1 }}
        >
          <Typography variant="subtitle2" fontWeight={700}>
            Medications
          </Typography>
          <Button startIcon={<AddIcon />} size="small" onClick={addMed}>
            Add medication
          </Button>
        </Stack>

        {medications.map((med, idx) => (
          <Box
            key={idx}
            sx={{
              p: 1.5,
              mb: 1,
              borderRadius: 1,
              bgcolor: "grey.50",
              border: "1px solid",
              borderColor: "grey.200",
            }}
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mb: 1 }}
            >
              <Typography variant="caption" fontWeight={700}>
                Medication {idx + 1}
              </Typography>
              {medications.length > 1 && (
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => removeMed(idx)}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </Stack>
            <Stack spacing={1}>
              <TextField
                label="Drug name"
                value={med.drugName}
                onChange={(e) => updateMed(idx, "drugName", e.target.value)}
                size="small"
                fullWidth
                required
                placeholder="e.g. Amoxicillin"
              />
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                <TextField
                  label="Dosage"
                  value={med.dosage}
                  onChange={(e) => updateMed(idx, "dosage", e.target.value)}
                  size="small"
                  fullWidth
                  placeholder="e.g. 500mg"
                />
                <TextField
                  label="Frequency"
                  value={med.frequency}
                  onChange={(e) => updateMed(idx, "frequency", e.target.value)}
                  size="small"
                  fullWidth
                  placeholder="e.g. Twice daily"
                />
                <TextField
                  label="Duration"
                  value={med.duration}
                  onChange={(e) => updateMed(idx, "duration", e.target.value)}
                  size="small"
                  fullWidth
                  placeholder="e.g. 5 days"
                />
              </Stack>
              <TextField
                label="Instructions"
                value={med.instructions}
                onChange={(e) => updateMed(idx, "instructions", e.target.value)}
                size="small"
                fullWidth
                placeholder="e.g. After food, with warm water"
              />
            </Stack>
          </Box>
        ))}

        <Divider sx={{ my: 2 }} />

        <TextField
          label="Diagnostic referral (lab tests, imaging, etc.)"
          value={diagnosticReferral}
          onChange={(e) => setDiagnosticReferral(e.target.value)}
          fullWidth
          multiline
          rows={2}
          sx={{ mb: 2 }}
          placeholder="e.g. CBC, Chest X-Ray, Thyroid panel"
        />

        <TextField
          label="Additional remarks"
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          fullWidth
          multiline
          rows={2}
          placeholder="e.g. Follow up in 5 days if no improvement. Drink plenty of fluids."
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          onClick={() => skipMut.mutate()}
          disabled={submitMut.isPending || skipMut.isPending}
        >
          {skipMut.isPending ? "Completing…" : "Skip prescription"}
        </Button>
        <Button
          variant="contained"
          disabled={!canSubmit}
          onClick={() => submitMut.mutate()}
        >
          {submitMut.isPending ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            "Save & Complete"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
